/**
 * `/orders` — read-only unfulfilled-order queue (Shopify → DTF, S1).
 *
 * The one job of this page: let the shop see, at a glance, what's waiting to be
 * printed across every connected store and what's blocking it. Newest first.
 * Read-only in S1 — mapping (S2), batching + gang sheets (S3) build on top.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  AlertTriangle,
  FileX,
  Inbox,
  Layers,
  Loader2,
  MoreHorizontal,
  PackageCheck,
  PackageOpen,
  Printer,
  RefreshCw,
  Scroll,
} from 'lucide-react';
import { Fragment, useEffect, useId, useMemo, useState } from 'react';
import { PageContainer } from '@/components/page-container';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { cn } from '@/lib/utils';
import { memorySafeMaxLengthIn } from './batch-lib';
import { PLACEMENT_LOCATION_OPTIONS } from './catalog-lib';
import { CompletedOrdersView } from './completed-orders-view';
import { blankLabel, variantLabel } from './inventory-lib';
import { orderCardSearch } from './order-detail-lib';
import {
  ALREADY_PRINTED_REASONS,
  type AlreadyPrintedReason,
  BatchBuildError,
  DECORATION_META,
  type DecorationFilter,
  decorationCounts,
  decorationOf,
  dueStatus,
  extractLineReasons,
  type FitCandidate,
  filterByDecoration,
  fitCandidates,
  groupByOrder,
  ITEM_CAP_WARN_AT,
  keepOnlyTechnique,
  type LineRef,
  lineKey,
  MAX_BATCH_ITEMS,
  type OrderGroup,
  type OrderLine,
  orphanSelectionKeys,
  pickInStockUpToCap,
  placementCountOf,
  type QueueSummary,
  relativeAge,
  type SheetOrder,
  STATUS_META,
  selectedGarmentCount,
  selectedItemCount,
  selectionStoreId,
  sheetContents,
  shouldReconcileSelection,
  sortQueue,
  summarizeQueue,
  toggleGroupSelection,
  toggleLineSelection,
} from './orders-queue';
import { BUILD_COPY, type BuildKind, buildKindFor, buildLandingRoute } from './screen-print-lib';

const PAGE_LIMIT = 100;

/** Outcome of a "Select in stock" click — what got selected and why the rest was left out. */
type InStockResult = {
  selectedLines: number;
  items: number;
  shortSkipped: number;
  capDeferred: number;
  oversized: number;
  truncated: boolean;
  /** Set only in the multi-store view, to name which store the pick scoped to. */
  storeId?: string;
};

export function OrdersPage() {
  const client = useApiClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  // ?view=completed gives the finished-orders review lane a linkable address (production-records R0,
  // the catalog ?filter= pattern). The queue below stays byte-identical in the default view.
  const urlView = (useSearch({ strict: false }) as { view?: string }).view;
  const [view, setView] = useState<'open' | 'completed'>(
    urlView === 'completed' ? 'completed' : 'open',
  );
  const switchView = (v: 'open' | 'completed') => {
    setView(v);
    navigate({
      to: '/orders',
      search: v === 'completed' ? { view: 'completed' } : {},
      replace: true,
    });
  };
  const [store, setStore] = useState<string>('all');
  // Technique filter (screen-print lane PR-3). Feeds `lines`, so it narrows the SELECTION helpers too —
  // see filterByDecoration for why that is the point rather than a side effect.
  const [technique, setTechnique] = useState<DecorationFilter>('all');
  const [selected, setSelected] = useState<Map<string, LineRef>>(new Map());
  const [inStockSummary, setInStockSummary] = useState<InStockResult | null>(null);

  // Cursor-paginated (100/page). Load ALL pages so the whole open-work queue is visible + selectable
  // for batching, and the per-store counts are accurate.
  const query = useInfiniteQuery({
    queryKey: ['orders', 'unfulfilled'],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await client.GET('/orders', {
        params: { query: { status: 'unfulfilled', limit: PAGE_LIMIT, cursor: pageParam } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load orders.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allLines = (query.data?.pages.flatMap((p) => p.items) ?? []) as OrderLine[];
  const storeOptions = useMemo(() => summarizeQueue(allLines).storeIds, [allLines]);
  // What the CURRENT SELECTION would build — drives the button + dialog copy so neither claims "gang
  // sheet" for a screen-print run. Derived over allLines (not the filtered view) for the same reason
  // `itemCount` is: the selection is what Build submits, whatever filter is showing.
  const selectedBuildKind = useMemo(
    () => buildKindFor(allLines.filter((l) => selected.has(lineKey(l)))),
    [allLines, selected],
  );
  // A selected line can LEAVE the queue under the operator — another station batches it, a reconcile
  // fulfils it, a sweep closes it — and `/orders` repaints from the change feed with no click. See
  // `orphanSelectionKeys` for the three different ways the derived numbers then lie.
  //
  // ⚠️ Tracked as a SET of keys, never a running count: StrictMode double-invokes this effect in dev,
  // and re-adding the same keys is idempotent where `n + orphans.length` would double-report.
  const [droppedKeys, setDroppedKeys] = useState<ReadonlySet<string>>(() => new Set());
  // ⚠️ `allLines` GROWS page by page while the fetch-all loop above is still running, so reconciling
  // before the queue has settled would deselect work that has simply not loaded yet.
  const queueSettled = shouldReconcileSelection({
    isSuccess: query.isSuccess,
    hasNextPage,
    isFetching: query.isFetching,
  });
  useEffect(() => {
    if (!queueSettled) return;
    const orphans = orphanSelectionKeys(selected, allLines);
    if (orphans.length === 0) return;
    const gone = new Set(orphans);
    setSelected((prev) => new Map([...prev].filter(([k]) => !gone.has(k))));
    setDroppedKeys((prev) => new Set([...prev, ...orphans]));
  }, [queueSettled, allLines, selected]);

  // Prioritize the queue: rush ↓, dueDate ↑, then FIFO. Client-side over the fully-loaded queue (the
  // page fetches every page above) — the server GSI stays FIFO. Sort feeds summary/readyLines/groups.
  const lines = sortQueue(
    filterByDecoration(
      store === 'all' ? allLines : allLines.filter((l) => l.storeId === store),
      technique,
    ),
  );
  const summary = useMemo(() => summarizeQueue(lines), [lines]);
  // Counted over the store-filtered set BEFORE the technique filter: a chip showing its own filtered
  // count would read 0 on every method except the active one, which is the opposite of the point.
  const techniqueCounts = useMemo(
    () =>
      decorationCounts(store === 'all' ? allLines : allLines.filter((l) => l.storeId === store)),
    [allLines, store],
  );

  const readyLines = lines.filter((l) => l.mappingStatus === 'MAPPED');
  // A gang sheet is single-store (never mixes brands), so "select all ready" scopes to ONE store:
  // the current selection's store, else the active store filter, else the first ready line's store
  // (oldest FIFO) in the all-stores view. With a single connected store this is just "all ready".
  const selStore = selectionStoreId(selected);
  const selectAllStore = selStore ?? (store !== 'all' ? store : readyLines[0]?.storeId);
  const scopedReady = selectAllStore ? readyLines.filter((l) => l.storeId === selectAllStore) : [];
  // The master checkbox reflects the FULL visible ready set (not just scopedReady), so it never shows
  // a solid check while another store's ready lines stay unselected — with 2+ stores a single-store
  // selection can't cover them all. The click action (toggleAllReady) still scopes to one store.
  const allReadySelected =
    readyLines.length > 0 && readyLines.every((l) => selected.has(lineKey(l)));

  const groups = useMemo(() => groupByOrder(lines), [lines]);

  // Finished-garment shelf advisory (plan PR-3): which visible lines the shelf could ship WHOLE.
  // Advisory only — reserves nothing (the select-in-stock doctrine); a failed read renders no chips.
  // The server allocates across EVERY open candidate line, so two pages can't both claim a shirt.
  const stockRefs = useMemo(
    () =>
      lines
        .filter((l) => l.mappingStatus === 'MAPPED')
        .slice(0, 500)
        .map((l) => ({ storeId: l.storeId, orderId: l.orderId, lineId: l.lineId })),
    [lines],
  );
  const stockMatch = useQuery({
    queryKey: ['garment-stock', 'match', 'orders', stockRefs.map((r) => r.lineId).join(',')],
    enabled: stockRefs.length > 0,
    queryFn: async () => {
      const { data, error } = await client.POST('/garment-stock/match', {
        body: { lineRefs: stockRefs },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to check the garment shelf.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.lines;
    },
  });
  const stockByLine = useMemo(() => {
    const m = new Map<string, { artDrift: boolean }>();
    for (const v of stockMatch.data ?? []) {
      if (v.coverable) m.set(lineKey(v), { artDrift: v.artDrift === true });
    }
    return m;
  }, [stockMatch.data]);
  // Batch items the selection will expand to (Σ placements), counted over ALL loaded lines so the
  // count matches exactly what Build submits ([...selected.values()]) regardless of the store filter.
  const itemCount = useMemo(() => selectedItemCount(allLines, selected), [allLines, selected]);
  // SCREENS the selection would burn. Fetched rather than derived: a screen is
  // (designId, location, sizeBand), and the queue carries no per-placement data at all — and could
  // not, because the effective screen set unions the mapping's `method` with the operator's per-build
  // choice, and because a screen is SHARED across lines so no per-line number could be summed.
  //
  // Only on a filmless selection: it is the one state that shows the number. Above the route's 500-ref
  // limit the query is skipped entirely — a truncated count would UNDERSTATE the screens to burn,
  // which is the failure direction that strands a run mid-press.
  const screenRefs = useMemo(() => [...selected.values()], [selected]);
  const screensQuery = useQuery({
    queryKey: [
      'screen-preview',
      screenRefs
        .map((r) => lineKey(r))
        .sort()
        .join(','),
    ],
    enabled:
      selectedBuildKind === 'screen-print' && screenRefs.length > 0 && screenRefs.length <= 500,
    queryFn: async () => {
      const { data, error } = await client.POST('/batches/screen-preview', {
        // Empty on purpose: the per-build "screen print (off this sheet)" ticks live in the Build
        // DIALOG, and none has been chosen while the bar is what's showing. A wholly screen-print
        // selection is already screen-print by its mapping, so the toggle would add nothing here.
        body: { lineRefs: screenRefs, screenPrintLocations: [] },
      });
      // Advisory: a failed read shows NO screens number, never an error and never a zero — the panel
      // degrades to garments alone, which is still true.
      if (error || !data?.ok) return null;
      return data.data;
    },
  });

  // Garments the selection presses. Scoped over allLines for the same reason `itemCount` is: the
  // selection is what Build submits, whatever filter happens to be showing.
  const garmentCount = useMemo(
    () => selectedGarmentCount(allLines, selected),
    [allLines, selected],
  );

  // Panel content. All derived over allLines for the same reason itemCount is — the selection is what
  // Build submits, whatever filter happens to be showing.
  const onSheetContents = useMemo(() => sheetContents(allLines, selected), [allLines, selected]);
  const selStoreId = selectionStoreId(selected);
  const candidates = useMemo(
    () => fitCandidates(allLines, selected, MAX_BATCH_ITEMS - itemCount, selStoreId),
    [allLines, selected, itemCount, selStoreId],
  );
  // The two halves of a refused mixed selection, so each fix button can say what it keeps.
  const spLineCount = useMemo(
    () =>
      allLines.filter((l) => selected.has(lineKey(l)) && decorationOf(l) === 'screen-print').length,
    [allLines, selected],
  );
  const overCap = itemCount > MAX_BATCH_ITEMS;

  // All three enforce the single-store invariant (a sheet can't mix brands) via the pure helpers —
  // selecting a line/order from a different store starts a fresh single-store selection.
  const toggle = (line: OrderLine) => setSelected((prev) => toggleLineSelection(prev, line));
  // Select (or clear) all of one order's ready lines at once — "batch the whole order."
  const toggleGroup = (group: OrderGroup) =>
    setSelected((prev) => toggleGroupSelection(prev, group));
  const toggleAllReady = () => {
    // scopedReady is already a single store's ready lines, so this is a plain toggle-all.
    setSelected((prev) => {
      if (scopedReady.length === 0) return prev;
      const allSel = scopedReady.every((l) => prev.has(lineKey(l)));
      const next = new Map(prev);
      for (const l of scopedReady) {
        if (allSel) next.delete(lineKey(l));
        else next.set(lineKey(l), { storeId: l.storeId, orderId: l.orderId, lineId: l.lineId });
      }
      return next;
    });
  };

  const [buildOpen, setBuildOpen] = useState(false);

  const createBatch = useMutation({
    mutationFn: async (opts: {
      refs: LineRef[];
      rollId?: string;
      lengthIn?: number;
      screenPrintLocations?: string[];
      pullFromStock?: boolean;
    }) => {
      const res = await client.POST('/batches', {
        body: {
          lineRefs: opts.refs,
          rollId: opts.rollId,
          lengthIn: opts.lengthIn,
          screenPrintLocations: opts.screenPrintLocations ?? [],
          pullFromStock: opts.pullFromStock ?? false,
        },
      });
      if (res.error) {
        const detail = res.error?.error?.detail ?? 'Failed to create the batch.';
        throw new BatchBuildError(detail, extractLineReasons(res.error));
      }
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: (batch) => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['batches'] });
      setSelected(new Map());
      setInStockSummary(null);
      setBuildOpen(false);
      // A filmless run lives on /screen-print; /batches filters runs OUT, so landing there after
      // building one shows a list that cannot contain it and reads as a failed build. A gang sheet
      // opens ITSELF on the workbench (/batches/:batchId) — the compositing banner is the first thing
      // the operator sees, instead of a list with nothing selected and the new row sorted last.
      if (buildLandingRoute(batch) === '/batches') {
        navigate({ to: '/batches/$batchId', params: { batchId: batch.batchId } });
      } else {
        navigate({ to: '/screen-print' });
      }
    },
  });

  // One-click "select what's in stock": ask the blank-coverage advisory which of THIS store's ready
  // lines can be delivered from blanks on hand (it allocates the shared pool committed/oldest-first
  // across the whole queue, so an already-built-but-unprinted batch's blanks are already spoken for),
  // then select the buildable ones in work-priority order up to the sheet's item cap. Advisory-only:
  // it reserves nothing and writes nothing (real consumption is at mark-printed) — it just replaces
  // the tedious select-everything-then-discover-the-shortfall-at-Build loop.
  const inStockPick = useMutation({
    mutationFn: async (ready: OrderLine[]) => {
      const checked = ready.slice(0, 500); // the coverage endpoint takes at most 500 refs
      const { data, error } = await client.POST('/inventory/coverage', {
        body: {
          lineRefs: checked.map((l) => ({
            storeId: l.storeId,
            orderId: l.orderId,
            lineId: l.lineId,
          })),
        },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to check blank stock.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      // Short lines are grouped under their blank; flatten to a "can't deliver this line" lookup.
      const shortKeys = new Set<string>();
      for (const b of data.data.blanks) for (const ln of b.lines) shortKeys.add(lineKey(ln));
      return {
        pick: pickInStockUpToCap(checked, shortKeys),
        truncated: ready.length > checked.length,
      };
    },
    onSuccess: ({ pick, truncated }, ready) => {
      setSelected(new Map(pick.refs.map((r) => [lineKey(r), r])));
      setInStockSummary({
        selectedLines: pick.refs.length,
        items: pick.items,
        shortSkipped: pick.shortSkipped,
        capDeferred: pick.capDeferred,
        oversized: pick.oversized,
        truncated,
        storeId: storeOptions.length > 1 ? ready[0]?.storeId : undefined,
      });
    },
    onError: () => setInStockSummary(null),
  });

  // Re-derive one order's mapping against the current catalog (after mapping the product), instead
  // of waiting for the ≤15-min reconcile. On success, refresh the queue so the flip is visible now.
  const recheck = useMutation({
    mutationFn: async (g: { storeId: string; orderId: string }) => {
      const res = await client.POST('/orders/{storeId}/{orderId}/recheck', {
        params: { path: { storeId: g.storeId, orderId: g.orderId } },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Re-check failed.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });

  // Mark a line already-printed off-system (e.g. a manual gang sheet): it leaves the queue + blank
  // demand and can never be nested. Confirmed via a dialog (the reason is audit-only). On success the
  // line drops out of the queue, so also clear it from any pending selection.
  const [markTarget, setMarkTarget] = useState<OrderLine | null>(null);
  const markPrinted = useMutation({
    mutationFn: async (v: { line: OrderLine; reason: AlreadyPrintedReason }) => {
      const res = await client.POST('/orders/{storeId}/{orderId}/lines/{lineId}/already-printed', {
        params: {
          path: { storeId: v.line.storeId, orderId: v.line.orderId, lineId: v.line.lineId },
        },
        body: { alreadyPrinted: true, reason: v.reason },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to mark it printed.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onSuccess: (_data, v) => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      setSelected((prev) => {
        const next = new Map(prev);
        next.delete(lineKey(v.line));
        return next;
      });
      setMarkTarget(null);
    },
  });

  // Bulk-mark the whole selection already-printed off-system (go-live cutover). One reason for the
  // batch; a client-side sequential loop over the SAME reviewed per-line endpoint — idempotent + off
  // the money path, so a partial failure is a safe re-run. Succeeded lines drop from the queue +
  // selection; any that failed stay selected so the operator can retry.
  const [bulkMarkOpen, setBulkMarkOpen] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const bulkMarkPrinted = useMutation({
    mutationFn: async (v: { refs: LineRef[]; reason: AlreadyPrintedReason }) => {
      const succeeded: LineRef[] = [];
      const failed: LineRef[] = [];
      setBulkProgress({ done: 0, total: v.refs.length });
      for (const ref of v.refs) {
        const res = await client.POST(
          '/orders/{storeId}/{orderId}/lines/{lineId}/already-printed',
          {
            params: { path: { storeId: ref.storeId, orderId: ref.orderId, lineId: ref.lineId } },
            body: { alreadyPrinted: true, reason: v.reason },
          },
        );
        if (res.error || !res.data?.ok) failed.push(ref);
        else succeeded.push(ref);
        setBulkProgress((p) => (p ? { done: p.done + 1, total: p.total } : p));
      }
      return { succeeded, failed };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      const done = new Set(result.succeeded.map((r) => lineKey(r)));
      setSelected((prev) => new Map([...prev].filter(([k]) => !done.has(k))));
      if (result.failed.length === 0) setBulkMarkOpen(false);
    },
    onSettled: () => setBulkProgress(null),
  });

  // The review lane (all hooks above have run — the queue queries keep warming in the background).
  if (view === 'completed') {
    return (
      <PageContainer className="space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Finished orders</h1>
            <p className="text-sm text-muted-foreground">
              Fulfilled and cancelled orders — the review lane. Search by order #, garment, or SKU;
              open one to see (or record) its production history.
            </p>
          </div>
          <OrdersViewToggle view="completed" onSwitch={switchView} />
        </header>
        <CompletedOrdersView />
      </PageContainer>
    );
  }

  return (
    <PageContainer className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Unfulfilled orders</h1>
          <p className="text-sm text-muted-foreground">
            Open line items across every connected store — rush first, then by due date, then
            oldest; work the top down.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <OrdersViewToggle view="open" onSwitch={switchView} />
          {storeOptions.length > 1 && (
            <Select
              value={store}
              onValueChange={(v) => {
                // Clear the selection when the store view changes — a selection is scoped to one
                // store (a sheet can't mix brands), so lines picked under a different filter must
                // not silently carry over and end up on another brand's sheet.
                setStore(v);
                setSelected(new Map());
                setInStockSummary(null);
              }}
            >
              <SelectTrigger className="w-40" aria-label="Filter by store">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stores</SelectItem>
                {storeOptions.map((id) => (
                  <SelectItem key={id} value={id}>
                    {id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => inStockPick.mutate(scopedReady)}
            disabled={inStockPick.isPending || scopedReady.length === 0}
            title="Select the ready lines you can deliver from blanks on hand — fills one sheet up to the item cap"
          >
            {inStockPick.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <PackageCheck className="size-4" />
            )}
            Select in stock
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={cn('size-4', query.isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </header>

      {query.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load orders</AlertTitle>
          <AlertDescription>
            {query.error instanceof Error ? query.error.message : 'Network error.'} Try Refresh — if
            it keeps failing, check that a store is connected and the API is reachable.
          </AlertDescription>
        </Alert>
      )}

      {query.isPending && <LoadingState />}

      {query.isSuccess && (
        <>
          {/* THE WORKBENCH. ⚠️ `min-w-0` on the queue column is load-bearing: a rigid 372px track
              beside `minmax(0,1fr)` starves the flexible one, and without it the queue table's own
              min-width pushes the grid wider than the viewport instead of scrolling inside its
              column. Two columns only at xl (1280px) — that is the width at which the table's
              720px minimum still fits beside the panel; below it the panel stacks under the queue
              and stops being sticky. */}
          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_372px] xl:items-start">
            <div className="min-w-0 space-y-4">
              {/* ⚠️ Promoted from a w-44 Select to visible chips WITH counts. The owner's first
                  report of this feature was "there is no way to filter only screen print items": a
                  control that hides its options until opened, and never says how many lines each
                  holds, is a control nobody discovers. Rendered whenever the queue has any lines —
                  an empty result under "Screen print" is a real answer; an absent filter is not. */}
              <QueueBar summary={summary} />
              {allLines.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 border-b pb-3">
                  <span className="mr-0.5 text-xs text-muted-foreground">Print method</span>
                  {TECHNIQUE_CHIPS.map((c) => {
                    const on = technique === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        aria-pressed={on}
                        // The visible label and the count are adjacent nodes, so the computed name
                        // would run together as "All print methods4". Spelled out for assistive tech.
                        aria-label={`${c.label}, ${techniqueCounts[c.id]} line${
                          techniqueCounts[c.id] === 1 ? '' : 's'
                        }`}
                        onClick={() => {
                          // Clear the selection, exactly as the store filter does and for a stronger
                          // reason: a screen-print run is built ALONE (owner decision D2), so a
                          // selection assembled under one technique must never carry into another
                          // and reach Build as a mixed batch.
                          setTechnique(c.id);
                          setSelected(new Map());
                          setInStockSummary(null);
                        }}
                        className={cn(
                          'inline-flex h-[30px] items-center gap-1.5 rounded-lg border border-transparent px-2.5 text-[13px] text-muted-foreground transition-colors hover:bg-muted',
                          on && 'border-border bg-secondary font-medium text-foreground',
                          on &&
                            c.id === 'screen-print' &&
                            'border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300',
                        )}
                      >
                        {c.label}
                        <b className="font-semibold tabular-nums">{techniqueCounts[c.id]}</b>
                      </button>
                    );
                  })}
                </div>
              )}
              {lines.length === 0 ? (
                <EmptyState hasStores={storeOptions.length > 0} />
              ) : (
                <>
                  {createBatch.isError && (
                    <BatchBuildErrorAlert
                      title="Couldn't create the batch"
                      error={createBatch.error}
                      lines={lines}
                    />
                  )}
                  {recheck.isError && (
                    <Alert variant="destructive">
                      <AlertTitle>Re-check failed</AlertTitle>
                      <AlertDescription>
                        {recheck.error instanceof Error ? recheck.error.message : 'Try again.'} The
                        order's mapping was not refreshed — the catalog change (if any) still
                        applies on the next reconcile.
                      </AlertDescription>
                    </Alert>
                  )}
                  {inStockPick.isError && (
                    <Alert variant="destructive">
                      <AlertTitle>Couldn't check blank stock</AlertTitle>
                      <AlertDescription>
                        {inStockPick.error instanceof Error
                          ? inStockPick.error.message
                          : 'Try again.'}{' '}
                        Nothing was selected — your current selection is unchanged.
                      </AlertDescription>
                    </Alert>
                  )}
                  {inStockSummary && (
                    <InStockSummaryAlert
                      summary={inStockSummary}
                      onDismiss={() => setInStockSummary(null)}
                    />
                  )}
                  <QueueDropNotice
                    count={droppedKeys.size}
                    onDismiss={() => setDroppedKeys(new Set())}
                  />
                  <QueueGroups
                    groups={groups}
                    selected={selected}
                    onToggle={toggle}
                    onToggleGroup={toggleGroup}
                    hasReady={scopedReady.length > 0}
                    allReadySelected={allReadySelected}
                    onToggleAllReady={toggleAllReady}
                    onRecheck={(g) => recheck.mutate({ storeId: g.storeId, orderId: g.orderId })}
                    recheckingKey={
                      recheck.isPending && recheck.variables
                        ? `${recheck.variables.storeId}#${recheck.variables.orderId}`
                        : null
                    }
                    onMarkPrinted={(line) => setMarkTarget(line)}
                    stockByLine={stockByLine}
                  />
                  {isFetchingNextPage && (
                    <p className="flex items-center justify-center gap-1 text-center text-xs text-muted-foreground">
                      <Loader2 className="size-3 animate-spin" /> loading more…
                    </p>
                  )}
                </>
              )}
            </div>
            <aside className="xl:sticky xl:top-4">
              <BuildPanel
                buildKind={selectedBuildKind}
                count={selected.size}
                itemCount={itemCount}
                garmentCount={garmentCount}
                screenCount={screensQuery.data?.screens ?? null}
                screenRows={screensQuery.data?.rows ?? []}
                designCount={new Set((screensQuery.data?.rows ?? []).map((r) => r.designId)).size}
                storeLabel={selStoreId ?? 'no store yet'}
                onSheet={onSheetContents}
                candidates={candidates}
                spCount={spLineCount}
                filmCount={selected.size - spLineCount}
                overCap={overCap}
                buildPending={createBatch.isPending}
                bulkPending={bulkMarkPrinted.isPending}
                onAdd={(refs) =>
                  setSelected((prev) => {
                    const next = new Map(prev);
                    for (const r of refs) next.set(lineKey(r), r);
                    return next;
                  })
                }
                onBuild={() => setBuildOpen(true)}
                onClear={() => {
                  setSelected(new Map());
                  setInStockSummary(null);
                  setDroppedKeys(new Set());
                }}
                onKeepOnly={(keep) => {
                  setSelected((prev) => keepOnlyTechnique(allLines, prev, keep));
                  setInStockSummary(null);
                }}
                onMarkPrinted={() => setBulkMarkOpen(true)}
              />
            </aside>
          </div>
        </>
      )}

      <BuildBatchDialog
        open={buildOpen}
        buildKind={selectedBuildKind}
        count={selected.size}
        refs={[...selected.values()]}
        pending={createBatch.isPending}
        error={createBatch.isError ? createBatch.error : null}
        lines={lines}
        onClose={() => {
          if (!createBatch.isPending) {
            createBatch.reset();
            setBuildOpen(false);
          }
        }}
        onBuild={(rollId, lengthIn, screenPrintLocations, pullFromStock) =>
          createBatch.mutate({
            refs: [...selected.values()],
            rollId,
            lengthIn,
            screenPrintLocations,
            pullFromStock,
          })
        }
      />

      <MarkPrintedDialog
        line={markTarget}
        pending={markPrinted.isPending}
        error={markPrinted.isError ? markPrinted.error : null}
        onConfirm={(reason) => markTarget && markPrinted.mutate({ line: markTarget, reason })}
        onClose={() => {
          if (!markPrinted.isPending) {
            markPrinted.reset();
            setMarkTarget(null);
          }
        }}
      />

      <BulkMarkPrintedDialog
        open={bulkMarkOpen}
        count={selected.size}
        pending={bulkMarkPrinted.isPending}
        progress={bulkProgress}
        failedCount={bulkMarkPrinted.data?.failed.length ?? 0}
        onConfirm={(reason) => bulkMarkPrinted.mutate({ refs: [...selected.values()], reason })}
        onClose={() => {
          if (!bulkMarkPrinted.isPending) {
            bulkMarkPrinted.reset();
            setBulkMarkOpen(false);
          }
        }}
      />
    </PageContainer>
  );
}

/**
 * The BUILD PANEL — the sheet workbench's right-hand column, and the design the owner picked.
 *
 * ⚠️ It renders ALWAYS, including with nothing selected. That is the point: the panel is what tells
 * an operator what a selection WILL produce before they commit to it, and a surface that only appears
 * after the first tick cannot teach anything. The floating pill it replaces could not hold this —
 * a `px-4 py-2` rounded bar has no room for what is on the sheet, what would still fit, or what has
 * to be burned — which is why every one of those was simply absent until now.
 *
 * Four states, one per {@link BuildKind}. Exported for `test/unit/orders-build-panel.test.tsx`.
 */
/** The print-method filter chips, in the order the queue is worked. */
const TECHNIQUE_CHIPS: { id: DecorationFilter; label: string }[] = [
  { id: 'all', label: 'All print methods' },
  { id: 'dtf', label: 'DTF' },
  { id: 'screen-print', label: 'Screen print' },
  { id: 'mixed', label: 'Screen print + DTF' },
];

type ScreenRow = NonNullable<components['schemas']['ScreenPreviewResponse']>['rows'][number];

export function BuildPanel({
  buildKind,
  count,
  itemCount,
  garmentCount,
  screenCount,
  screenRows,
  designCount,
  storeLabel,
  onSheet,
  candidates,
  spCount,
  filmCount,
  overCap,
  buildPending,
  bulkPending,
  onAdd,
  onBuild,
  onClear,
  onKeepOnly,
  onMarkPrinted,
}: {
  buildKind: BuildKind;
  count: number;
  itemCount: number;
  garmentCount: number;
  screenCount: number | null;
  screenRows: ScreenRow[];
  /** Distinct designs on a filmless run — >1 means the press breaks down mid-run. */
  designCount: number;
  storeLabel: string;
  onSheet: SheetOrder[];
  candidates: FitCandidate[];
  spCount: number;
  filmCount: number;
  overCap: boolean;
  buildPending: boolean;
  bulkPending: boolean;
  onAdd: (refs: LineRef[]) => void;
  onBuild: () => void;
  onClear: () => void;
  onKeepOnly: (keep: 'screen-print' | 'film') => void;
  onMarkPrinted: () => void;
}) {
  const copy = BUILD_COPY[buildKind];
  const cta = `${copy.cta}${count > 0 ? ` (${count})` : ''}`;
  const headroom = MAX_BATCH_ITEMS - itemCount;
  const nearCap = !overCap && itemCount >= ITEM_CAP_WARN_AT;
  const pct = Math.min(100, Math.round((itemCount / MAX_BATCH_ITEMS) * 100));
  const tickPct = Math.round((ITEM_CAP_WARN_AT / MAX_BATCH_ITEMS) * 100);
  const toneText = overCap
    ? 'text-destructive'
    : nearCap
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-muted-foreground';

  const footer = (disabled: boolean, dest: string) => (
    <div className="space-y-2 border-t pt-3">
      <Button size="lg" className="w-full" disabled={disabled} onClick={onBuild}>
        {buildPending ? <Loader2 className="size-4 animate-spin" /> : <Layers className="size-4" />}
        {cta}
      </Button>
      <p className="text-center text-xs text-muted-foreground">{dest}</p>
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="flex-1"
          onClick={onClear}
          disabled={bulkPending}
        >
          Clear selection
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={onMarkPrinted}
          disabled={buildPending || bulkPending}
          title="Mark the selected garments as already printed off-system"
        >
          {bulkPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Printer className="size-4" />
          )}
          Mark printed
        </Button>
      </div>
    </div>
  );

  const header = (title: string, tone: string, withStore: boolean) => (
    <div className="flex items-center gap-2 border-b px-4 py-3">
      <Layers className={cn('size-4', tone)} />
      <h3 className="text-sm font-semibold">{title}</h3>
      {withStore && (
        <Badge variant="outline" className="ml-auto font-normal">
          {storeLabel}
        </Badge>
      )}
    </div>
  );

  if (buildKind === 'none') {
    return (
      <div className="rounded-lg border bg-card">
        {header('Nothing selected', 'text-muted-foreground', false)}
        <p className="px-4 py-4 text-sm text-muted-foreground">
          Tick a size, or a whole order, and this panel becomes the thing you are about to build — a
          film gang sheet with its {MAX_BATCH_ITEMS}-item budget, or a filmless screen-print run.
        </p>
      </div>
    );
  }

  if (buildKind === 'mixed') {
    return (
      <div className="rounded-lg border border-destructive/50 bg-card">
        {header(copy.title, 'text-destructive', false)}
        <div className="space-y-3 p-4">
          <p className="text-sm text-muted-foreground">{copy.blurb}</p>
          {/* The fix, offered rather than described — each half says how much it keeps, so the
              operator can see which one is the work they meant. */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onKeepOnly('screen-print')}
              className="rounded-md border px-3 py-2 text-left transition-colors hover:bg-accent"
            >
              <span className="block text-sm font-medium">Keep screen print</span>
              <span className="block text-xs text-muted-foreground">
                {spCount} line{spCount === 1 ? '' : 's'}
              </span>
            </button>
            <button
              type="button"
              onClick={() => onKeepOnly('film')}
              className="rounded-md border px-3 py-2 text-left transition-colors hover:bg-accent"
            >
              <span className="block text-sm font-medium">Keep film</span>
              <span className="block text-xs text-muted-foreground">
                {filmCount} line{filmCount === 1 ? '' : 's'}
              </span>
            </button>
          </div>
          {footer(true, 'Build stays off until the selection is one technique.')}
        </div>
      </div>
    );
  }

  if (buildKind === 'screen-print') {
    return (
      <div className="rounded-lg border bg-card">
        {header(copy.title, 'text-violet-600 dark:text-violet-400', true)}
        <div className="space-y-3 p-4">
          <div>
            <div className="flex items-baseline gap-1.5">
              <b className="text-3xl font-semibold tabular-nums leading-9 tracking-tight">
                {garmentCount}
              </b>
              <span className="text-xs text-muted-foreground">
                garment{garmentCount === 1 ? '' : 's'}
                {screenCount === null
                  ? ''
                  : ` · ${screenCount} screen${screenCount === 1 ? '' : 's'}`}
              </span>
            </div>
          </div>
          <p className="flex items-start gap-2 rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
            <FileX className="mt-px size-3.5 shrink-0" />
            No film — nothing to nest, and no roll to pick.
          </p>
          {designCount > 1 && (
            <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
              <AlertTriangle className="mt-px size-3.5 shrink-0" />
              This run mixes {designCount} designs, so the press breaks down between them.
            </p>
          )}
          {screenRows.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Screens to burn
              </p>
              <ul className="space-y-1">
                {screenRows.map((r) => (
                  <li
                    key={`${r.designId}|${r.location}|${r.sizeBand}`}
                    className="flex items-baseline justify-between gap-2 text-sm"
                  >
                    <span className="min-w-0 truncate">
                      <span className="font-medium capitalize">{r.location}</span>
                      <span className="text-muted-foreground">
                        {' · '}
                        {r.designName ?? r.designId}
                        {' · '}
                        {r.sizeBand === 'youth' ? 'Youth' : 'Adult'}
                      </span>
                    </span>
                    <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
                      {r.garments} through
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {footer(
            buildPending || bulkPending,
            'Lands on Screen print, born READY — press it, then close the run.',
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card">
      {header(copy.title, 'text-muted-foreground', true)}
      <div className="space-y-3 p-4">
        {/* The 99-item budget, as a budget. A bar with the warn tick ON it answers "how much room is
            left" at a glance, which a bare "74 / 99" makes the operator compute. */}
        <div>
          <div className="flex items-baseline gap-1.5">
            <b className="text-3xl font-semibold tabular-nums leading-9 tracking-tight">
              {itemCount}
            </b>
            <span className="text-xs text-muted-foreground">of {MAX_BATCH_ITEMS} print items</span>
          </div>
          <div className="relative mt-2.5 h-2 overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full transition-[width] duration-200',
                overCap ? 'bg-destructive' : nearCap ? 'bg-amber-500' : 'bg-emerald-500',
              )}
              style={{ width: `${pct}%` }}
            />
            <div
              className="absolute top-0 h-full w-px bg-foreground/40"
              style={{ left: `${tickPct}%` }}
              aria-hidden="true"
            />
          </div>
          <p className={cn('mt-1.5 text-xs', toneText)}>
            {overCap
              ? `${itemCount - MAX_BATCH_ITEMS} items over — drop something before you build`
              : headroom === 0
                ? 'Full — this sheet is packed'
                : `${headroom} item${headroom === 1 ? '' : 's'} of headroom left`}
          </p>
        </div>
        {onSheet.length > 0 && (
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              On this sheet
            </p>
            <ul className="space-y-0.5">
              {onSheet.map((o) =>
                o.designs.map((g) => (
                  <li
                    key={`${o.orderName}#${g.title}`}
                    className="flex items-baseline gap-2 text-sm"
                  >
                    <span className="shrink-0 font-medium">{o.orderName}</span>
                    <span className="min-w-0 flex-1 truncate text-muted-foreground">{g.title}</span>
                    <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
                      {g.items} item{g.items === 1 ? '' : 's'}
                    </span>
                  </li>
                )),
              )}
            </ul>
          </div>
        )}
        {candidates.length > 0 && (
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Would still fit
            </p>
            <ul className="space-y-1">
              {candidates.map((c) => (
                <li key={c.key} className="flex items-center gap-2 text-sm">
                  <span className="min-w-0 flex-1 truncate text-muted-foreground">{c.label}</span>
                  <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
                    {c.need} item{c.need === 1 ? '' : 's'}
                  </span>
                  {c.fits ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 px-2"
                      onClick={() => onAdd(c.refs)}
                    >
                      Add
                    </Button>
                  ) : (
                    <span className="shrink-0 text-xs text-muted-foreground">next sheet</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {footer(
          buildPending || bulkPending || overCap,
          'Lands on Gang sheets as a DRAFT, then composites.',
        )}
      </div>
    </div>
  );
}

/**
 * Confirm marking a garment already-printed off-system (e.g. on a manual gang sheet). This keeps it off
 * the gang sheet and out of blank demand — so the copy is explicit that it must ALREADY be done. The
 * reason is captured for audit only; it drives no inventory (on-hand is reconciled by a physical count).
 */
function MarkPrintedDialog({
  line,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  line: OrderLine | null;
  pending: boolean;
  error: unknown;
  onConfirm: (reason: AlreadyPrintedReason) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<AlreadyPrintedReason>('MANUAL_SHEET');
  // Reset the reason to the default each time the dialog opens on a new line.
  useEffect(() => {
    if (line) setReason('MANUAL_SHEET');
  }, [line]);

  return (
    <Dialog open={line != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark printed off-system</DialogTitle>
          <DialogDescription>
            This garment will <span className="font-medium">not</span> be put on a gang sheet. Only
            do this if the transfer is already done.
          </DialogDescription>
        </DialogHeader>
        {line && (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
            <div className="font-medium">
              {line.orderName} · {line.title}
            </div>
            {line.variantTitle && (
              <div className="text-xs text-muted-foreground">{line.variantTitle}</div>
            )}
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Why is it already printed?</Label>
          <Select value={reason} onValueChange={(v) => setReason(v as AlreadyPrintedReason)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ALREADY_PRINTED_REASONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {error != null && (
          <Alert variant="destructive">
            <AlertTitle>Couldn't mark it printed</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : 'Try again.'}
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(reason)} disabled={pending}>
            {pending ? <Loader2 className="size-4 animate-spin" /> : <Printer className="size-4" />}
            Mark printed off-system
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Confirm bulk-marking the whole selection already-printed off-system (go-live cutover). One reason for
 * the batch; shows live progress during the sequential run. On a partial failure the dialog stays open —
 * the un-marked lines remain selected — so "Mark {count} printed" simply retries the rest.
 */
function BulkMarkPrintedDialog({
  open,
  count,
  pending,
  progress,
  failedCount,
  onConfirm,
  onClose,
}: {
  open: boolean;
  count: number;
  pending: boolean;
  progress: { done: number; total: number } | null;
  failedCount: number;
  onConfirm: (reason: AlreadyPrintedReason) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<AlreadyPrintedReason>('MANUAL_SHEET');
  useEffect(() => {
    if (open) setReason('MANUAL_SHEET');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark printed off-system</DialogTitle>
          <DialogDescription>
            These {count} garment{count === 1 ? '' : 's'} will{' '}
            <span className="font-medium">not</span> be put on a gang sheet. Only do this if the
            transfers are already done.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>Why are they already printed?</Label>
          <Select
            value={reason}
            onValueChange={(v) => setReason(v as AlreadyPrintedReason)}
            disabled={pending}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ALREADY_PRINTED_REASONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {pending && progress && (
          <p className="text-sm text-muted-foreground" aria-live="polite">
            Marking {progress.done} / {progress.total}…
          </p>
        )}
        {!pending && failedCount > 0 && (
          <Alert variant="destructive">
            <AlertTitle>{failedCount} couldn't be marked</AlertTitle>
            <AlertDescription>
              They're still in the queue (nothing was lost). Try again to mark the rest.
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(reason)} disabled={pending || count === 0}>
            {pending ? <Loader2 className="size-4 animate-spin" /> : <Printer className="size-4" />}
            Mark {count} printed
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Batch-create error, rendered per line when the API gave us line-level context. Each blocked/
 * conflicting line is named by its order + item (joined from the queue) so the operator knows
 * exactly which line to fix and why — instead of a bare "N line(s) are not MAPPED…" summary.
 */
function BatchBuildErrorAlert({
  title,
  error,
  lines,
}: {
  title: string;
  error: unknown;
  lines: OrderLine[];
}) {
  const reasons = error instanceof BatchBuildError ? error.reasons : [];
  const labelFor = (ref: LineRef) => {
    const line = lines.find(
      (l) => l.storeId === ref.storeId && l.orderId === ref.orderId && l.lineId === ref.lineId,
    );
    if (!line) return ref.lineId;
    return line.variantTitle
      ? `${line.orderName} · ${line.title} (${line.variantTitle})`
      : `${line.orderName} · ${line.title}`;
  };
  return (
    <Alert variant="destructive">
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        {reasons.length > 0 ? (
          <div className="space-y-1">
            <p>These lines can't be batched yet:</p>
            <ul className="list-disc space-y-1 pl-4">
              {reasons.map((r) => (
                <li key={`${r.storeId}#${r.orderId}#${r.lineId}`}>
                  <span className="font-medium">{labelFor(r)}</span> — {r.reason}
                </li>
              ))}
            </ul>
          </div>
        ) : error instanceof Error ? (
          error.message
        ) : (
          'Try again.'
        )}
      </AlertDescription>
    </Alert>
  );
}

type RollSummary = components['schemas']['RollSummary'];

/**
 * Effective per-sheet max = the roll's physical max, further capped so one sheet's canvas fits the
 * compositor's memory (mirrors the server clamp). Longer jobs spill to more, shorter sheets.
 * Module-level (pure, no component state) so it's a stable effect dependency.
 */
const rollMaxLen = (r: RollSummary) =>
  Math.min(r.maxLengthIn, memorySafeMaxLengthIn(r.usableWidthIn));

/**
 * Pick the film roll + sheet length to nest the selected lines onto, then build the gang sheet — or,
 * for a FILMLESS screen-print run, confirm a build that needs neither.
 *
 * Exported for `test/unit/orders-build-dialog.test.tsx`: the film-only controls and the copy that
 * calls a run a sheet are exactly the kind of thing no logic test can see.
 */
export function BuildBatchDialog({
  open,
  buildKind,
  count,
  refs,
  pending,
  error,
  lines,
  onClose,
  onBuild,
}: {
  open: boolean;
  /** What this selection actually builds — the title/CTA follow it (screen-print lane fix). */
  buildKind: BuildKind;
  count: number;
  refs: LineRef[];
  pending: boolean;
  error: unknown;
  lines: OrderLine[];
  onClose: () => void;
  onBuild: (
    rollId: string | undefined,
    lengthIn: number | undefined,
    screenPrintLocations: string[],
    pullFromStock: boolean,
  ) => void;
}) {
  const client = useApiClient();
  /**
   * A filmless screen-print run: no film to nest, no roll to pick, no sheet to carve placements out
   * of. Hides the film-only controls and swaps the copy that calls a run a sheet.
   *
   * ⚠️ A PREDICTION, not the server's answer. `buildKind` comes from each line's persisted
   * `decoration`; the server re-reads the product mapping FRESH at build and decides `kind` from
   * "resolved zero DTF items". Between a mapping change and the ≤15-min reconcile the two can
   * disagree. That is why this hides controls but relaxes NO guard: the roll is still picked, still
   * sent, and the rolls pending/error gates still hold — so a line that turns out to need film after
   * all still nests onto the operator's roll, never the 22″ engine default.
   */
  const filmless = buildKind === 'screen-print';
  // Locations to produce by screen print for THIS batch (excluded from the sheet, and — since a
  // screen-printed placement needs no film — excluded from the shelf advisory below). Re-picked each
  // open; never carried over from a previous build.
  const [screenPrint, setScreenPrint] = useState<string[]>([]);
  // Opt-in to PULL on-shelf transfers from stock instead of reprinting them (printed-transfer Phase B).
  // Default off — the feature biases to reprinting; pulling draws down materials, so staff confirm it.
  const [pullFromStock, setPullFromStock] = useState(false);
  // ADVISORY (printed-transfer Phase 1): do the selected lines need designs we already have printed on
  // the shelf? Read-only — it changes nothing about the build; it just surfaces what could be pulled
  // instead of reprinted. Fallback-safe on the server, so this can never block a build.
  const matchQuery = useQuery({
    queryKey: [
      'transfer-match',
      refs
        .map((r) => `${r.storeId}#${r.orderId}#${r.lineId}`)
        .sort()
        .join(','),
      // Re-run as the operator toggles screen-print locations: a screen-printed placement drops off
      // the shelf advisory (it needs no film).
      [...screenPrint].sort().join(','),
    ],
    enabled: open && refs.length > 0,
    queryFn: async () => {
      const { data, error: err } = await client.POST('/print-inventory/match', {
        body: {
          lineRefs: refs.map((r) => ({ storeId: r.storeId, orderId: r.orderId, lineId: r.lineId })),
          screenPrintLocations: screenPrint,
        },
      });
      if (err) throw new Error(err?.error?.detail ?? 'Failed to check the shelf.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  const shelf = matchQuery.data;
  // ADVISORY (blank coverage): can the selection be delivered from blank stock? Honest shared-pool
  // allocation across the whole open queue. Read-only; fallback-safe on the server, so it never blocks.
  const coverageQuery = useQuery({
    queryKey: [
      'blank-coverage',
      refs
        .map((r) => `${r.storeId}#${r.orderId}#${r.lineId}`)
        .sort()
        .join(','),
    ],
    enabled: open && refs.length > 0,
    queryFn: async () => {
      const { data, error: err } = await client.POST('/inventory/coverage', {
        body: {
          lineRefs: refs.map((r) => ({ storeId: r.storeId, orderId: r.orderId, lineId: r.lineId })),
        },
      });
      if (err) throw new Error(err?.error?.detail ?? 'Failed to check blank stock.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  const coverage = coverageQuery.data;
  const rollsQuery = useQuery({
    queryKey: ['rolls'],
    queryFn: async () => {
      const { data, error: err } = await client.GET('/rolls');
      if (err) throw new Error(err?.error?.detail ?? 'Failed to load rolls.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.items as RollSummary[];
    },
    enabled: open,
  });
  const rolls = rollsQuery.data ?? [];

  const [rollId, setRollId] = useState<string>('');
  const [length, setLength] = useState<string>('');
  const screenPrintId = useId();

  // On open (once rolls load), preselect the default roll + its effective max length.
  useEffect(() => {
    if (!open || rolls.length === 0) return;
    const def = rolls.find((r) => r.isDefault) ?? rolls[0];
    if (def) {
      setRollId(def.rollId);
      setLength(String(rollMaxLen(def)));
    }
  }, [open, rolls]);

  // Reset the per-build picks whenever the dialog opens (the dialog stays mounted between builds).
  useEffect(() => {
    if (open) {
      setScreenPrint([]);
      setPullFromStock(false);
    }
  }, [open]);

  const toggleScreenPrint = (loc: string, on: boolean) =>
    setScreenPrint((prev) => (on ? [...prev, loc] : prev.filter((l) => l !== loc)));

  const selectedRoll = rolls.find((r) => r.rollId === rollId);
  const maxLen = selectedRoll ? rollMaxLen(selectedRoll) : 240;
  // True when the memory cap is the binding limit (below the roll's physical max) — worth explaining.
  const memoryCapped = selectedRoll != null && maxLen < selectedRoll.maxLengthIn;
  const lenNum = Number(length);
  const lenValid = lenNum > 0 && lenNum <= maxLen;

  function pickRoll(id: string) {
    setRollId(id);
    const r = rolls.find((x) => x.rollId === id);
    if (r) setLength(String(rollMaxLen(r))); // reset length to the newly-picked roll's effective max
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* max-h + scroll: the shelf/coverage advisories render one row per blank/design and can grow
          past the viewport, pushing the Build button off-screen. Cap + scroll so the footer stays
          reachable — matches blank-detail/manual-po/add-stock/transfer-inventory dialogs. */}
      <DialogContent className="max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>{BUILD_COPY[buildKind].title}</DialogTitle>
          <DialogDescription>
            {filmless
              ? BUILD_COPY[buildKind].blurb
              : `Nest ${count} selected line${count === 1 ? '' : 's'} onto a film roll. Width is fixed by the roll; pick the sheet length for this job.`}
          </DialogDescription>
        </DialogHeader>

        {coverage && coverage.shortLineCount > 0 && (
          <Alert className="border-amber-300 dark:border-amber-900/60">
            <AlertTitle className="text-amber-800 dark:text-amber-300">
              Short on blanks — {coverage.shortLineCount} line
              {coverage.shortLineCount === 1 ? '' : 's'} ({coverage.shortUnits} garment
              {coverage.shortUnits === 1 ? '' : 's'}) can’t be delivered from stock
            </AlertTitle>
            <AlertDescription>
              <p className="text-sm">
                You can’t press what you don’t have. Cancel and deselect these lines (or reorder the
                blanks) before building. Blanks are shared across the queue — older and
                already-batched orders get first call on stock.
              </p>
              <ul className="mt-2 space-y-1.5 text-sm">
                {coverage.blanks.map((b) => (
                  <li key={b.ssSku}>
                    <div className="font-medium">
                      {blankLabel(b)}
                      {variantLabel(b) ? (
                        <span className="text-muted-foreground"> · {variantLabel(b)}</span>
                      ) : null}
                    </div>
                    <div className="tabular-nums text-muted-foreground">
                      need {b.neededBySelection},{' '}
                      {b.availableToSelection === b.onHand
                        ? `${b.onHand} on hand`
                        : `${b.availableToSelection} available of ${b.onHand} on hand`}
                      {b.onOrder > 0 ? ` · ${b.onOrder} on order` : ''} →{' '}
                      <span className="font-semibold text-amber-800 dark:text-amber-300">
                        {b.short} short
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {b.lines
                        .map((l) => `${l.orderName ?? l.orderId} (short ${l.shortBy})`)
                        .join(', ')}
                    </div>
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {!filmless && shelf && shelf.rows.length > 0 && (
          <Alert>
            <AlertTitle>
              Already on the shelf — {shelf.totalCoverable} transfer
              {shelf.totalCoverable === 1 ? '' : 's'} you could pull instead of reprinting
            </AlertTitle>
            <AlertDescription>
              <p className="text-sm">
                These selected designs have printed transfers on hand. Pull them from stock to leave
                those placements off the sheet and press them from the existing films instead of
                reprinting — the shelf is decremented (reversible: deleting the batch returns them).
              </p>
              <label
                htmlFor={`${screenPrintId}-pull`}
                className="mt-2 flex items-center gap-2 text-sm font-medium"
              >
                <Checkbox
                  id={`${screenPrintId}-pull`}
                  checked={pullFromStock}
                  onCheckedChange={(v) => setPullFromStock(v === true)}
                />
                Pull these from stock instead of reprinting
              </label>
              <ul className="mt-2 space-y-0.5 text-sm">
                {shelf.rows.map((r) => (
                  <li key={`${r.designId}#${r.sizeBand}`} className="tabular-nums">
                    <span className="font-medium">{r.designName ?? r.designId}</span>{' '}
                    <span className="text-muted-foreground">
                      ({r.sizeBand === 'youth' ? 'Youth' : 'Adult'})
                    </span>{' '}
                    — need {r.needed}, have {r.onHand} → pull {r.coverable}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {rollsQuery.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : rollsQuery.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Couldn't load rolls</AlertTitle>
            <AlertDescription>
              {rollsQuery.error instanceof Error ? rollsQuery.error.message : 'Network error.'} Try
              again — building is paused so the sheet doesn't fall back to the 22″ default while
              your rolls are unavailable.
            </AlertDescription>
          </Alert>
        ) : filmless ? null : rolls.length === 0 ? (
          <Alert>
            <AlertTitle>No film rolls registered</AlertTitle>
            <AlertDescription>
              This sheet will nest onto the 22″ default. Register your actual film in{' '}
              <Link to="/settings/film-rolls" className="font-medium underline">
                Film rolls
              </Link>{' '}
              to nest at its real width.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Film roll</Label>
              <Select value={rollId} onValueChange={pickRoll}>
                {/* w-full min-w-0: the shadcn trigger defaults to w-fit + nowrap, so a long roll name
                    grows it past the modal (and widens the whole dialog). Fill + let the value truncate. */}
                <SelectTrigger className="w-full min-w-0">
                  <SelectValue placeholder="Choose a roll" />
                </SelectTrigger>
                <SelectContent>
                  {rolls.map((r) => (
                    <SelectItem key={r.rollId} value={r.rollId}>
                      <span className="inline-flex items-center gap-2">
                        <Scroll className="size-3.5" />
                        {r.name} — {r.usableWidthIn}″ wide{r.isDefault ? ' · default' : ''}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="build-length">Sheet length (in) — max {maxLen}″ for this roll</Label>
              <Input
                id="build-length"
                type="number"
                inputMode="decimal"
                step="1"
                min="1"
                max={maxLen}
                value={length}
                onChange={(e) => setLength(e.target.value)}
              />
              {selectedRoll && (
                <p className="text-xs text-muted-foreground">
                  Nesting onto {selectedRoll.usableWidthIn}″ × up to {lenValid ? lenNum : maxLen}″;
                  overflow spills to more sheets.
                  {memoryCapped && (
                    <>
                      {' '}
                      Capped at {maxLen}″/sheet (below the roll's {selectedRoll.maxLengthIn}″) so
                      each sheet renders reliably — longer jobs just use more sheets.
                    </>
                  )}
                </p>
              )}
            </div>
          </div>
        )}

        {!filmless && (
          <div className="space-y-1.5">
            <Label>Screen print (off this sheet)</Label>
            <div className="flex flex-wrap gap-x-4 gap-y-2 rounded-md border p-3">
              {PLACEMENT_LOCATION_OPTIONS.map((loc) => (
                <label
                  key={loc}
                  htmlFor={`${screenPrintId}-${loc}`}
                  className="flex items-center gap-2 text-sm"
                >
                  <Checkbox
                    id={`${screenPrintId}-${loc}`}
                    checked={screenPrint.includes(loc)}
                    onCheckedChange={(v) => toggleScreenPrint(loc, v === true)}
                  />
                  {loc}
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Placements at these locations are produced by screen print and left off the gang sheet
              — for every line in this batch. Leave all unchecked to print everything via DTF.
            </p>
          </div>
        )}

        {error != null && (
          <BatchBuildErrorAlert
            title={filmless ? "Couldn't build the run" : "Couldn't build the sheet"}
            error={error}
            lines={lines}
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onBuild(
                rolls.length === 0 ? undefined : rollId || undefined,
                rolls.length === 0 ? undefined : lenNum,
                screenPrint,
                // Only meaningful when the shelf panel offered transfers; harmless otherwise (the
                // server pulls only DTF-chosen placements whose film is actually on hand).
                pullFromStock && (shelf?.rows.length ?? 0) > 0,
              )
            }
            // Block building until the rolls query settles — otherwise a click during load/error
            // (rolls === []) would silently nest onto the 22″ default even though rolls are registered.
            disabled={
              pending ||
              // Unreachable today (the selection bar's Build is disabled on a mixed selection), but
              // this dialog must never be the thing that sends a mix to the server.
              buildKind === 'mixed' ||
              rollsQuery.isPending ||
              rollsQuery.isError ||
              (rolls.length > 0 && (!rollId || !lenValid))
            }
          >
            {pending ? <Loader2 className="size-4 animate-spin" /> : <Layers className="size-4" />}
            {BUILD_COPY[buildKind].cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Result of a "Select in stock" click — how many lines it selected and why the rest were left out
 * (short on blanks / didn't fit the one-sheet item cap / too big to nest on any single sheet). The
 * selection itself is already applied; this is purely informational and dismissible.
 */
/**
 * Told when selected lines leave the queue under the operator.
 *
 * ⚠️ Rendered OUTSIDE the selection bar deliberately. That bar only exists while something is
 * selected, and the case that matters most is the LAST selected line vanishing — which empties the
 * selection and would take the explanation away with it, leaving the counts to change with no reason
 * given. Exported for the render test: a missing explanation is invisible to a logic test.
 */
export function QueueDropNotice({ count, onDismiss }: { count: number; onDismiss: () => void }) {
  if (count === 0) return null;
  const one = count === 1;
  return (
    <Alert>
      <AlertTitle>
        {count} selected line{one ? '' : 's'} left the queue and {one ? 'was' : 'were'} deselected
      </AlertTitle>
      <AlertDescription>
        <p className="text-sm">
          {one ? 'It was' : 'They were'} batched, closed or fulfilled somewhere else while you were
          choosing. The selection count and the item total now match what Build would submit.
        </p>
        <Button variant="ghost" size="sm" className="mt-2 h-7 px-2" onClick={onDismiss}>
          Dismiss
        </Button>
      </AlertDescription>
    </Alert>
  );
}

function InStockSummaryAlert({
  summary,
  onDismiss,
}: {
  summary: InStockResult;
  onDismiss: () => void;
}) {
  const { selectedLines, items, shortSkipped, capDeferred, oversized, truncated, storeId } =
    summary;
  const notes: string[] = [];
  if (shortSkipped > 0) {
    notes.push(`${shortSkipped} skipped — short on blanks (not enough stock to deliver).`);
  }
  if (capDeferred > 0) {
    notes.push(
      `${capDeferred} more in stock didn't fit the ${MAX_BATCH_ITEMS}-item sheet — build this batch, then click again for the rest.`,
    );
  }
  if (oversized > 0) {
    notes.push(
      `${oversized} line${oversized === 1 ? '' : 's'} won't fit one sheet alone — split ${
        oversized === 1 ? 'it' : 'them'
      } across batches manually.`,
    );
  }
  if (truncated) notes.push('Only the first 500 ready lines were checked.');

  const none = selectedLines === 0;
  return (
    <Alert className={none ? undefined : 'border-emerald-300 dark:border-emerald-900/60'}>
      <AlertTitle className={none ? undefined : 'text-emerald-800 dark:text-emerald-300'}>
        {none
          ? 'Nothing in stock to select right now'
          : `Selected ${selectedLines} line${selectedLines === 1 ? '' : 's'} in stock — ${items} print item${
              items === 1 ? '' : 's'
            }${storeId ? ` · ${storeId}` : ''}`}
      </AlertTitle>
      <AlertDescription>
        {notes.length > 0 ? (
          <ul className="list-disc space-y-1 pl-4 text-sm">
            {notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm">Everything ready fits on this sheet.</p>
        )}
        <Button variant="ghost" size="sm" className="mt-2 h-7 px-2" onClick={onDismiss}>
          Dismiss
        </Button>
      </AlertDescription>
    </Alert>
  );
}

/**
 * The queue's own stat bar — five numbers on one compact row, INSIDE the queue column.
 *
 * Replaces four full-width card tiles that sat above the whole page and so read as page furniture
 * rather than as a description of the list beneath them. The order count is new: the queue is worked
 * order by order, and a line count alone says nothing about how many JOBS are waiting.
 */
function QueueBar({ summary }: { summary: QueueSummary }) {
  const stats: { label: string; value: number; tone?: 'warn' | 'bad' }[] = [
    { label: summary.orderCount === 1 ? 'order' : 'orders', value: summary.orderCount },
    { label: summary.lineCount === 1 ? 'line item' : 'line items', value: summary.lineCount },
    { label: 'pieces', value: summary.pieceCount },
    { label: 'blocked', value: summary.blocked, tone: summary.blocked > 0 ? 'bad' : undefined },
    { label: 'unpaid', value: summary.unpaid, tone: summary.unpaid > 0 ? 'warn' : undefined },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border bg-card px-3.5 py-2.5">
      {stats.map((st, i) => (
        <Fragment key={st.label}>
          {i > 0 && <span aria-hidden="true" className="hidden h-[18px] w-px bg-border sm:block" />}
          <span className="flex items-baseline gap-1.5">
            <b
              className={cn(
                'text-base font-semibold tabular-nums',
                st.tone === 'bad' && 'text-destructive',
                st.tone === 'warn' && 'text-amber-600 dark:text-amber-400',
              )}
            >
              {st.value}
            </b>
            <span className="text-xs text-muted-foreground">{st.label}</span>
          </span>
        </Fragment>
      ))}
    </div>
  );
}

function QueueGroups({
  groups,
  selected,
  onToggle,
  onToggleGroup,
  hasReady,
  allReadySelected,
  onToggleAllReady,
  onRecheck,
  recheckingKey,
  onMarkPrinted,
  stockByLine,
}: {
  groups: OrderGroup[];
  selected: Map<string, LineRef>;
  onToggle: (line: OrderLine) => void;
  onToggleGroup: (group: OrderGroup) => void;
  hasReady: boolean;
  allReadySelected: boolean;
  onToggleAllReady: () => void;
  onRecheck: (group: OrderGroup) => void;
  /** `storeId#orderId` of the order currently being re-checked (spinner), or null. */
  recheckingKey: string | null;
  /** Open the "mark printed off-system" confirm for one line. */
  onMarkPrinted: (line: OrderLine) => void;
  /** Lines the finished-garment shelf could ship whole (plan PR-3 advisory) — chip only. */
  stockByLine: ReadonlyMap<string, { artDrift: boolean }>;
}) {
  const now = Date.now();
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 px-1">
        <Checkbox
          aria-label="Select all ready lines"
          checked={allReadySelected}
          disabled={!hasReady}
          onCheckedChange={() => onToggleAllReady()}
        />
        <span className="text-xs text-muted-foreground">Select all ready</span>
      </div>
      {groups.map((group) => {
        const groupReady = group.readyLines.length;
        const groupSelected = group.readyLines.filter((l) => selected.has(lineKey(l))).length;
        const groupAllSelected = groupReady > 0 && groupSelected === groupReady;
        const groupOverCap = group.itemCount > MAX_BATCH_ITEMS;
        const due = dueStatus(group.dueDate, now);
        return (
          <section
            key={group.key}
            className={cn(
              'overflow-hidden rounded-lg border bg-card',
              // ⚠️ A rush order gets a 3px DESTRUCTIVE left edge — findable while scrolling, which a
              // pill mid-row is not.
              group.rush && 'border-l-[3px] border-l-destructive',
              // ⚠️ "has picks" is a NEUTRAL emphasis, deliberately NOT --primary as the mockup had
              // it: --primary and --destructive are near-identical reds in this app, so a primary
              // border on a rush card would show red meaning two different things at once. The
              // mockup made the same call one rule earlier for the selected ROW.
              groupSelected > 0 && 'border-foreground/30',
            )}
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 bg-muted px-3 py-2">
              <Checkbox
                aria-label={
                  groupReady > 0
                    ? `Select order ${group.orderName}`
                    : 'No ready lines in this order'
                }
                // ⚠️ A partly-picked order reads INDETERMINATE at the control the operator is
                // looking at. A plain boolean showed an empty box, i.e. "nothing selected here",
                // with the truth relegated to muted text further along the row.
                checked={groupAllSelected ? true : groupSelected > 0 ? 'indeterminate' : false}
                disabled={groupReady === 0}
                onCheckedChange={() => onToggleGroup(group)}
              />
              <Link
                to="/orders/$storeId/$orderId"
                params={{ storeId: group.storeId, orderId: group.orderId }}
                search={orderCardSearch('orders')}
                className="font-semibold hover:underline"
              >
                {group.orderName}
              </Link>
              <span className="rounded bg-background/60 px-1.5 py-0.5 text-xs text-muted-foreground">
                {group.storeId}
              </span>
              {group.rush && (
                <Badge
                  variant="outline"
                  className="border-destructive/40 bg-destructive/10 text-destructive"
                >
                  Rush
                </Badge>
              )}
              {due && (
                <span
                  className={
                    due.overdue
                      ? 'text-xs font-semibold text-destructive'
                      : 'text-xs text-muted-foreground'
                  }
                >
                  {due.label}
                </span>
              )}
              <span
                className="text-xs text-muted-foreground"
                title={new Date(group.shopifyCreatedAt).toLocaleString()}
              >
                {relativeAge(group.shopifyCreatedAt, now)}
              </span>
              <span className="text-xs text-muted-foreground">
                · {groupReady} of {group.lines.length} ready
                {groupSelected > 0 && groupSelected < groupReady
                  ? ` (${groupSelected} selected)`
                  : ''}
              </span>
              {groupOverCap && (
                <span className="text-xs font-medium text-destructive">
                  · {group.itemCount} items — exceeds the {MAX_BATCH_ITEMS} cap, split across
                  batches
                </span>
              )}
              {group.lines.some((l) => l.mappingStatus === 'UNMAPPED') && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-6 px-2 text-xs"
                  disabled={recheckingKey === group.key}
                  onClick={() => onRecheck(group)}
                  title="Re-derive this order's mapping against the catalog — use after you map its product"
                >
                  {recheckingKey === group.key ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <RefreshCw className="size-3" />
                  )}
                  Re-check
                </Button>
              )}
            </div>
            <ul>
              {group.lines.map((line) => {
                const meta = STATUS_META[line.mappingStatus];
                const ready = line.mappingStatus === 'MAPPED';
                const key = lineKey(line);
                const on = selected.has(key);
                const dec = DECORATION_META[decorationOf(line)];
                const shelf = stockByLine.get(key);
                const items = placementCountOf(line);
                return (
                  <li
                    key={key}
                    className={cn(
                      'flex items-start gap-2.5 border-t px-3 py-1.5 transition-colors',
                      // ⚠️ A NEUTRAL wash, never --primary: with 40 lines picked a primary-tinted
                      // queue reads as 40 errors.
                      on ? 'bg-foreground/[0.04]' : 'hover:bg-muted/45',
                    )}
                  >
                    <Checkbox
                      className="mt-0.5"
                      aria-label={
                        ready ? `Select ${line.title}` : 'Only mapped lines can be batched'
                      }
                      checked={on}
                      disabled={!ready}
                      onCheckedChange={() => onToggle(line)}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm" title={line.title}>
                        {line.title}
                      </div>
                      {line.variantTitle && (
                        <div className="text-xs text-muted-foreground">{line.variantTitle}</div>
                      )}
                    </div>
                    <span className="hidden w-[132px] shrink-0 truncate pt-0.5 font-mono text-xs text-muted-foreground sm:block">
                      {line.sku ?? '—'}
                    </span>
                    <span className="w-10 shrink-0 pt-0.5 text-right text-sm font-medium tabular-nums">
                      {line.unfulfilledQty}
                    </span>
                    {/* ⚠️ The cell that makes a filmless line legible ON THE ROW, and the one that
                        makes the 99-item budget attributable to the rows consuming it. */}
                    <span className="w-[58px] shrink-0 pt-1 text-right text-xs tabular-nums text-muted-foreground">
                      {items === 0 ? 'no film' : `${items} item${items === 1 ? '' : 's'}`}
                    </span>
                    <span className="flex w-[124px] shrink-0 flex-col items-start gap-1">
                      <Badge variant="outline" className={meta.className}>
                        {meta.label}
                      </Badge>
                      {/* DTF renders nothing — it is the default, and a badge on every row would be
                          noise hiding the two that matter. */}
                      {dec && (
                        <Badge variant="outline" className={`w-fit font-normal ${dec.className}`}>
                          {dec.label}
                        </Badge>
                      )}
                      {shelf && (
                        <Badge
                          variant="outline"
                          className="w-fit border-sky-500/40 bg-sky-500/10 font-normal text-sky-700 dark:text-sky-400"
                        >
                          on shelf{shelf.artDrift ? ' · art changed' : ''}
                        </Badge>
                      )}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 shrink-0"
                          aria-label={`Actions for ${line.title}`}
                        >
                          <MoreHorizontal className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onMarkPrinted(line)}>
                          <Printer className="size-4" />
                          Mark printed off-system
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function EmptyState({ hasStores }: { hasStores: boolean }) {
  const Icon = hasStores ? PackageOpen : Inbox;
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-center">
      <Icon className="size-8 text-muted-foreground" />
      <p className="text-sm font-medium">Nothing waiting to print</p>
      <p className="max-w-sm text-sm text-muted-foreground">
        {hasStores
          ? 'Every open line item has been fulfilled. New orders from a connected store show up here automatically.'
          : 'Connect a store and its unfulfilled orders will appear here as they come in.'}
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Skeleton className="h-[76px]" />
        <Skeleton className="h-[76px]" />
        <Skeleton className="h-[76px]" />
        <Skeleton className="h-[76px]" />
      </div>
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

/** Open queue ↔ Finished-orders review lane switch (production-records R0). */
function OrdersViewToggle({
  view,
  onSwitch,
}: {
  view: 'open' | 'completed';
  onSwitch: (v: 'open' | 'completed') => void;
}) {
  return (
    <div className="flex items-center rounded-lg border p-0.5">
      <Button
        variant={view === 'open' ? 'secondary' : 'ghost'}
        size="sm"
        className="h-7 px-3"
        onClick={() => onSwitch('open')}
      >
        Open
      </Button>
      <Button
        variant={view === 'completed' ? 'secondary' : 'ghost'}
        size="sm"
        className="h-7 px-3"
        onClick={() => onSwitch('completed')}
      >
        Completed
      </Button>
    </div>
  );
}
